import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Group, ActionIcon, Tooltip, Text, Avatar, Modal, Box, Button, Grid,
} from '@mantine/core';
import {
  IconRefresh, IconEye, IconPencil, IconTrash, IconPlus,
} from '@tabler/icons-react';
import { CCMSDataTable, TextInput } from '@/components';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import { toast } from '@/components/ui/providers/AlertProvider';
import CreateSessionDrawer from './CreateSessionDrawer';
import SessionDetails, { ColorBadge } from '../Components/SessionDetails';
import SessionNotesPanel from '../Components/SessionNotesPanel';
import { useCareSessionService, CareSessionModelView } from '@actschurch/ccms-sdk';

interface SessionTabProps {
  caseID: number;
  active: boolean;
  currentUserId?: string;
}

export default function SessionTab({ caseID, active, currentUserId }: SessionTabProps) {
  const Sessions = useCareSessionService();

  const [sessions, setSessions] = useState<CareSessionModelView[]>([]);
  const [loading, setLoading]   = useState(false);

  const [detailsOpen, setDetailsOpen]         = useState(false);
  const [selectedSession, setSelectedSession] = useState<CareSessionModelView | null>(null);

  const [searchTerm, setSearchTerm] = useState('');

  const [formOpen, setFormOpen]             = useState(false);
  const [editingSession, setEditingSession] = useState<CareSessionModelView | null>(null);

  const [sortConfig, setSortConfig]   = useState<SortConfig | null>(null);
  const [deletingID, setDeletingID]   = useState<number | null>(null);

  const fetchSessions = useCallback(async () => {
    if (!caseID) return;
    setLoading(true);
    try {
      const res: any = await Sessions.byCase(caseID);
      setSessions(res?.data ?? res ?? []);
    } catch (e) {
      console.error('Error fetching sessions:', e);
      toast.error('Error', 'Could not load sessions.');
    } finally {
      setLoading(false);
    }
  }, [caseID, Sessions]);

  useEffect(() => { if (active) fetchSessions(); }, [active, fetchSessions]);

  const openDetails = (session: CareSessionModelView) => {
    setSelectedSession(session);
    setDetailsOpen(true);
  };

  const openCreate = () => {
    setEditingSession(null);
    setFormOpen(true);
  };

  const openEdit = (session: CareSessionModelView) => {
    setEditingSession(session);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingSession(null);
  };

  const handleDeleteSession = async (session: CareSessionModelView) => {
    const label = session.name ?? 'this session';
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;

    setDeletingID(session.sessionID);
    try {
      const ok = await Sessions.delete(session.sessionID);
      if (ok) {
        toast.success('Session Deleted', `${label} has been deleted.`);
        if (selectedSession?.sessionID === session.sessionID) {
          setDetailsOpen(false);
          setSelectedSession(null);
        }
        await fetchSessions();
      } else {
        toast.error('Error', `Could not delete "${label}".`);
      }
    } catch (e) {
      console.error('Error deleting session:', e);
      toast.error('Error', `Could not delete "${label}". Please try again.`);
    } finally {
      setDeletingID(null);
    }
  };

  const sortedSessions = useMemo(() => {
    if (!sortConfig) return sessions;
    return [...sessions].sort((a, b) => {
      const getValue = (row: CareSessionModelView) => {
        switch (sortConfig.field) {
          case 'name':        return (row.name        ?? '').toLowerCase();
          case 'typeName':    return (row.typeName     ?? '').toLowerCase();
          case 'leaderName':  return (row.leaderName   ?? '').toLowerCase();
          case 'sessionDate': return String(row.sessionDateTime ?? '');
          case 'modeDisplay': return (row.modeDisplay  ?? '').toLowerCase();
          case 'statusName':  return (row.statusName   ?? '').toLowerCase();
          default:             return '';
        }
      };
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (aVal === bVal) return 0;
      return aVal > bVal
        ? (sortConfig.direction === 'asc' ? 1 : -1)
        : (sortConfig.direction === 'asc' ? -1 : 1);
    });
  }, [sessions, sortConfig]);

  const columns: TableColumn<CareSessionModelView>[] = [
    {
      key: 'name',
      header: 'Name',
      accessor: (row) => row.name ?? '—',
      sortable: true,
      render: (value) => <Text size="sm" fw={500}>{value as string}</Text>,
    },
    {
      key: 'typeName',
      header: 'Type',
      accessor: (row) => row.typeName ?? '—',
      sortable: true,
      render: (_, row) => <ColorBadge label={row.typeName ?? 'N/A'} color={row.typeColor} />,
    },
    {
      key: 'leaderName',
      header: 'Leader',
      accessor: (row) => row.leaderName ?? '—',
      sortable: true,
      render: (_, row) => (
        <Group gap={8} wrap="nowrap">
          <Avatar src={row.leaderAvatar} size="sm" radius="xl">
            {!row.leaderAvatar && (row.leaderName?.charAt(0).toUpperCase() ?? '?')}
          </Avatar>
          <Text size="sm">{row.leaderName ?? 'Unassigned'}</Text>
        </Group>
      ),
    },
    {
      key: 'sessionDate',
      header: 'Date',
      accessor: (row) => row.sessionDateDisplay ?? '—',
      sortable: true,
      render: (value) => <Text size="sm">{value as string}</Text>,
    },
    {
      key: 'modeDisplay',
      header: 'Mode',
      accessor: (row) => row.modeDisplay ?? '—',
      sortable: true,
      render: (value) => <Text size="sm" c="dimmed">{value as string}</Text>,
    },
    {
      key: 'statusName',
      header: 'Status',
      accessor: (row) => row.statusName ?? '—',
      sortable: true,
      align: 'center',
      render: (_, row) => <ColorBadge label={row.statusName ?? 'N/A'} color={row.statusColor} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: (row) => row,
      sortable: false,
      align: 'center',
      render: (_, row) => (
        <Group gap={4} justify="center" wrap="nowrap">
          <Tooltip label="View session">
            <ActionIcon variant="light" color="blue" onClick={(e) => { e.stopPropagation(); openDetails(row); }}>
              <IconEye size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Update session">
            <ActionIcon variant="light" color="gray" onClick={(e) => { e.stopPropagation(); openEdit(row); }}>
              <IconPencil size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete session">
            <ActionIcon
              variant="light"
              color="red"
              loading={deletingID === row.sessionID}
              onClick={(e) => { e.stopPropagation(); handleDeleteSession(row); }}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      ),
    },
  ];

  return (
    <>
      <Group justify="space-between" mb="md" gap="xs">
        <TextInput placeholder="Search sessions..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        <Group gap={4}>
          <Tooltip label="Refresh sessions" withArrow>
            <ActionIcon variant="subtle" onClick={fetchSessions} loading={loading} size="lg">
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Add Session
          </Button>
        </Group>
      </Group>

      <CCMSDataTable
        data={sortedSessions}
        columns={columns}
        loading={loading}
        sortable
        onSort={(config) => setSortConfig(config)}
        onRowClick={(row) => openDetails(row)}
        striped
        highlightOnHover
      />

      {selectedSession && (
        <Modal
          opened={detailsOpen}
          onClose={() => { setDetailsOpen(false); setSelectedSession(null); }}
          title={selectedSession.name ?? 'Session Details'}
          size="90%"
          centered
        >
          <Grid gutter="xl">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <SessionDetails session={selectedSession} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <SessionNotesPanel
                sessionID={selectedSession.sessionID}
                active={detailsOpen}
                currentUserId={currentUserId}
              />
            </Grid.Col>
          </Grid>
        </Modal>
      )}

      <CreateSessionDrawer
        opened={formOpen}
        onClose={closeForm}
        caseID={caseID}
        session={editingSession}
        onSaved={fetchSessions}
      />
    </>
  );
}